
import { Box, Step, StepLabel, Stepper, styled } from "@mui/material";
import { Ico_Correct, Ico_WarningSolid } from "@sagecentral/core-ui";
import useWorkItemDraftContext from "hooks/work-items/useWorkItemDraftContext";
import IStepItem from "models/work-items/IStepItem";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { toPascalCase } from "utils/string-utils";

type WorkItemCreateStepperProps = {
    activeStep: number;
    steps: IStepItem[];
    onStepClick: (step: number) => void;
};

const StepStyle = styled(Step)(({theme}) => ({
    textDecoration: 'none',
    pointerEvents: 'none',
    cursor: 'default',
    '& > :not(.Mui-disabled):not(.MuiStepConnector-root) > *': {
        cursor: 'pointer',
        pointerEvents: 'auto'
    },
    '& .Mui-completed': {
        '&.MuiSvgIcon-root': {
            color: theme.palette.success.main
        }
    },
    '& .Mui-warning': {
        '& .MuiSvgIcon-root': {
            color: theme.palette.warning.main
        }
    }
}));

const getStepErrorProps = (step: IStepItem, isActive: boolean) => {
    if(step.visited && isActive && step.complete){
        return{
            className: 'Mui-active-completed',
            icon: <Ico_Correct sx={{ height: '28px', width: '28px', stroke: '#008929' }} />
        }
    }

    if ((!step.visited || !step.fieldErrors))
        return {};

    let hasWarning = false;
    for (let key of Object.keys(step.fieldErrors)) {
        const error = step.fieldErrors[key][0];
        if (!error) continue;
        
        switch (error.type) {
            case 'error': 
                return { 
                    error: true,
                    icon: <Ico_WarningSolid color='error' />
                };
            case 'warn': 
                hasWarning = true;
                break;
        }
    }
    
    if (hasWarning) {
        return { 
            className: 'Mui-warning'
        };
    }

    return {};
}

const WorkItemCreateStepper = (props: WorkItemCreateStepperProps) => {
    const { activeStep, steps, onStepClick } = props;
    const { workItem } = useWorkItemDraftContext();
    const baseUrl = useMemo(() => 
        (workItem?.id || 0) > 0
            ? `/workitems/create/${workItem?.id}/`
            : `/workitems/create/`
    , [ workItem ]);
    
    return (
        <Box sx={{
            height: '120px'
        }}>
            <Helmet>
                <title>{`Sage Central | Create Work Item | ${ steps && activeStep != -1 && toPascalCase(steps[activeStep]?.label)}`}</title>
            </Helmet>
            <Stepper alternativeLabel>
                { steps.map((step, index) => (
                    <StepStyle key={index} 
                        active={index === activeStep}
                        completed={step.complete && step.visited && activeStep != index}
                        disabled={!step.enabled || index === activeStep}
                        onClick={() => onStepClick(index)}
                    >
                        <StepLabel {...getStepErrorProps(step, index === activeStep)}>
                            {index === activeStep ? 
                            <span style={{fontWeight: 'bold'}}>
                                {step.label}
                            </span>
                            : step.label}
                        </StepLabel>
                    </StepStyle>
                ))}
            </Stepper>
        </Box>
    )
};

export default WorkItemCreateStepper;